import React from 'react';
import I18n from 'i18n-js';
import { gql } from '@reverbdotcom/commons/src/gql';
import { useQuery } from '@reverbdotcom/commons/src/useQuery';
import { RCLoadingBars, RCModal, RCText } from '@reverbdotcom/cadence/components';
import { useSeller } from '@reverbdotcom/commons/src/user_hooks';
import { formatNumber } from '@reverbdotcom/commons/src/money';
import { MParticleEventName } from '@reverbdotcom/commons/src/elog/mparticle_types';
import { trackEvent } from '@reverbdotcom/commons/src/elog/mparticle_tracker';
import { LISTING_STATES } from '@reverbdotcom/commons/src/constants';
import { useMutation } from '@reverbdotcom/commons/src/useMutation';
import { Field } from '../sell/form/sellFormReducerTypes';
import { listingSellerPrice } from './quick_price_edit/quick_price_edit_button';
import { listingPriceRecommendation } from './quick_price_edit/quick_price_edit_modal';
import RenewListingPriceRecommendation from './RenewListingPriceRecommendation';
import SellFormIncVatPricingHint from '../sell/form/components/SellFormIncVatPricingHint';
import QuickPriceEditHelpText, { calculatePercentOff } from './quick_price_edit/quick_price_edit_help_text';
import { MoneyInput } from '@reverbdotcom/commons/src/components/money_input';
import PriceBreakdownV2 from '../sell/form/components/PriceBreakdownV2';
import useDebounce from '@reverbdotcom/commons/src/components/useDebounce';
import { Listing } from '@reverbdotcom/commons/src/gql/graphql';
import { parseMutationErrors } from '@reverbdotcom/commons/src/parse_graphql_errors';

interface IExternalProps {
  listing: Listing;
  isOpen: boolean;
  requestClose: React.Dispatch<void>;
  onSuccessCallback: (appliedPriceCents?: number) => void;
}

export const COMPONENT_NAME = 'RenewListingModal';
const DEFAULT_CURRENCY = 'USD';

export const RENEW_LISTING_MUTATION = gql(`
  mutation Core_RenewListing_PriceUpdate(
    $listingId: String!,
    $price: String!
  ) {
    renewListing(input: {
      listingId: $listingId
      price: $price
    }) {
      _id
      id
      title
      sellerPrice {
        amountCents
        currency
        amount
        display
        symbol
      }
      renewalEligibility {
        isEligible
        renewalCount
      }
    }
  }
`);

export const RENEW_LISTING_COPY = gql(`
  query Core_ListingRenewalCopy(
    $listingId: String!
  ) {
    listingRenewalCopy(input: { listingId: $listingId }) {
      generic {
        title
        subtitle
        recentOrdersSubtitle
        priceDropSubtitle
      }
    }
  }
`);

export default function RenewListingModal({
  listing,
  isOpen,
  requestClose,
  onSuccessCallback,
}: IExternalProps) {
  const seller = useSeller();

  const {
    sellerId,
    locale,
    shopCountryCode,
    shopCurrency,
  } = seller;

  const { data: renewalCopyData, loading: renewalCopyLoading } = useQuery(RENEW_LISTING_COPY, {
    skip: !isOpen,
    variables: {
      listingId: listing.id,
    },
  });

  const { title, subtitle, recentOrdersSubtitle, priceDropSubtitle } = renewalCopyData?.listingRenewalCopy?.generic ?? {};

  const [updateListingPrice, {
    loading: isUpdatingListingPrice,
  }] = useMutation(RENEW_LISTING_MUTATION);

  const [priceAmount, setPriceAmount] = React.useState('');
  // Estimated Earnings API is triggered by the money input.
  // This prevents the API from immediately firing if the user is in the middle of typing a number.
  const debouncedPriceAmount = useDebounce(priceAmount, 200);
  const [errors, setErrors] = React.useState([]);

  const originalSellerPrice = listingSellerPrice(listing, shopCurrency || DEFAULT_CURRENCY);
  const { currency: listingCurrency } = originalSellerPrice;

  const priceRecommendation = listingPriceRecommendation(listing);
  const { priceMiddle: competitivePrice } = priceRecommendation;

  const isLiveListing = listing.state == LISTING_STATES.LIVE;
  const forceMoneySuffixForUserInUS = locale === 'en' && shopCountryCode === 'US' && shopCurrency === 'USD';

  React.useEffect(() => {
    const shouldInitialize = isOpen && !renewalCopyLoading && !!listing;

    if (shouldInitialize) {
      trackEvent({
        eventName: MParticleEventName.ComponentView,
        componentName: COMPONENT_NAME,
        listingId: listing.id,
        sellerId,
      });

      setPriceAmount(
        formatNumber(
          Number(originalSellerPrice.amount),
        ),
      );
    }
  }, [renewalCopyLoading]);

  function handleCloseModal() {
    setErrors([]);
    requestClose();
  }

  function handleErrors(errors) {
    setErrors(errors.map(error => error.message));
  }

  function formatErrors() {
    return errors.join(' ');
  }

  async function updatePricing({
    priceAmount,
  }) {
    const response = await updateListingPrice({
      variables: {
        listingId: listing.id,
        price: priceAmount,
      },
    });

    if (response.errors?.length) {
      handleErrors(parseMutationErrors({ graphQLErrors: [...response.errors] }));
    } else if (response.data) {
      const updatedPrice = response.data.renewListing.sellerPrice;
      const priceDroppedAttribute = updatedPrice.amountCents != originalSellerPrice.amountCents ? {
        priceDropped: updatedPrice.amountCents < originalSellerPrice.amountCents,
      } : {};

      const discountPercent = calculatePercentOff(originalSellerPrice, updatedPrice.amount);
      const discountPercentAttribute = discountPercent ? { discountPercent } : {};

      const wellPricedAttribute = competitivePrice ? {
        wellPriced: competitivePrice.amountCents >= updatedPrice.amountCents,
      } : {};

      trackEvent({
        eventName: MParticleEventName.UpdatedListingPrice,
        componentName: COMPONENT_NAME,
        listingId: listing.id,
        sellerId,
        ...priceDroppedAttribute,
        ...discountPercentAttribute,
        ...wellPricedAttribute,
      });

      setErrors([]);
      onSuccessCallback(updatedPrice.amountCents);
    }
  }

  if (!isOpen) return null;

  return (
    <RCModal
      isOpen={isOpen}
      title={title || ''}
      onOpenChange={handleCloseModal}
      actions={{
        primary: {
          onClick: () => updatePricing({ priceAmount }),
          buttonText: I18n.t('discovery.listingsManagement.renewListing.modal.renewButton'),
          disabled: renewalCopyLoading || isUpdatingListingPrice || !priceAmount,
        },
        secondary: true,
      }}
    >
      {renewalCopyLoading ? (
        <div className="d-flex fx-justify-center">
          <RCLoadingBars />
        </div>
      ) : (
        <div className="renew-listing-modal__content">
          <RCText>{subtitle}</RCText>
          {recentOrdersSubtitle && <RCText.Default weight="bold">{recentOrdersSubtitle}</RCText.Default>}
          {priceDropSubtitle && <RCText.Default weight="bold">{priceDropSubtitle}</RCText.Default>}

          <div className="bg-color-gray-100 d-flex fx-dir-col padding-4 bd-radius-md">
            <MoneyInput
              id={Field.PRICE}
              name={Field.PRICE}
              label={I18n.t('discovery.listingsManagement.renewListing.modal.moneyInput.newPrice')}
              value={priceAmount || ''}
              onChange={event => setPriceAmount(event.target.value)}
              required
              helpText={<QuickPriceEditHelpText
                originalPrice={originalSellerPrice}
                currentPriceAmount={priceAmount}
                isLiveListing={isLiveListing} />
              }
              errorText={formatErrors()}
              currencyCode={listingCurrency}
              suffixText={forceMoneySuffixForUserInUS ? listingCurrency : null}
            />

            {listing.vatIncluded &&
              <SellFormIncVatPricingHint />
            }

            <div className="renew-listing-modal__price-breakdown">
              <PriceBreakdownV2
                listingId={listing.id}
                price={debouncedPriceAmount}
                currency={listingCurrency}
                shop={listing.shop}
                showIncVatPricingHint={listing.vatIncluded}
              />
            </div>
          </div>
          <RenewListingPriceRecommendation
            cspSlug={listing.csp?.slug}
            priceRecommendation={priceRecommendation}
            updatePrice={priceAmount => setPriceAmount(priceAmount)}
          />
        </div>
      )}
    </RCModal>
  );
}
